import { create } from 'zustand';

interface QuestionState {
  loading: boolean;
  added: boolean;
  error?: string;
}

interface AIQuestionStatesStore {
  questionStates: Record<string, QuestionState>;
  isAddingAll: boolean;
  aiGenerateLoading: boolean;
  setQuestionLoading: (questionId: string, loading: boolean) => void;
  setQuestionAdded: (questionId: string, added: boolean) => void;
  setQuestionError: (questionId: string, error: string | undefined) => void;
  getQuestionState: (questionId: string) => QuestionState;
  setIsAddingAll: (isAddingAll: boolean) => void;
  setAiGenerateLoading: (aiGenerateLoading: boolean) => void;
  resetQuestionState: (questionId: string) => void;
  resetAllQuestionStates: () => void;
  areAllQuestionsAdded: (questionIds: string[]) => boolean;
  resetQuestionStates: () => void;
}

export const useAIQuestionStatesStore = create<AIQuestionStatesStore>((set, get) => ({
  questionStates: {},
  isAddingAll: false,
  aiGenerateLoading: false,

  setQuestionLoading: (questionId: string, loading: boolean) => {
    set((state) => ({
      questionStates: {
        ...state.questionStates,
        [questionId]: {
          ...state.questionStates[questionId],
          loading,
        },
      },
    }));
  },

  setQuestionAdded: (questionId: string, added: boolean) => {
    set((state) => ({
      questionStates: {
        ...state.questionStates,
        [questionId]: {
          ...state.questionStates[questionId],
          added,
          loading: false, // Reset loading when setting added state
        },
      },
    }));
  },

  setQuestionError: (questionId: string, error: string | undefined) => {
    set((state) => ({
      questionStates: {
        ...state.questionStates,
        [questionId]: {
          ...state.questionStates[questionId],
          error,
          loading: false, // Reset loading when setting error
        },
      },
    }));
  },

  getQuestionState: (questionId: string) => {
    const state = get().questionStates[questionId];
    return state || { loading: false, added: false };
  },

  setIsAddingAll: (isAddingAll: boolean) => {
    set({ isAddingAll });
  },

  setAiGenerateLoading: (aiGenerateLoading: boolean) => {
    set({ aiGenerateLoading });
  },

  resetQuestionState: (questionId: string) => {
    set((state) => ({
      questionStates: {
        ...state.questionStates,
        [questionId]: { loading: false, added: false, error: undefined },
      },
    }));
  },

  resetAllQuestionStates: () => {
    set({ questionStates: {}, isAddingAll: false, aiGenerateLoading: false });
  },

  areAllQuestionsAdded: (questionIds: string[]) => {
    const { questionStates } = get();
    return questionIds.every((id) => questionStates[id]?.added === true);
  },

  resetQuestionStates: () => {
    set({
      questionStates: {},
      isAddingAll: false,
      aiGenerateLoading: false,
    });
  },
}));

export const selectResetQuestionStates = (state: AIQuestionStatesStore) =>
  state.resetQuestionStates;

export const selectAiGenerateLoading = (state: AIQuestionStatesStore) => state.aiGenerateLoading;
